/**
 * camTheme.js — MCPlayback camera-identity color preferences via localStorage.
 *
 * Mirrors util/theme.js (the appTheme dark/light pattern), but manages two
 * independent camera-color prefs that only have a visible effect inside
 * MCPlayback (the multi-cam timeline lanes + cell identity borders):
 *
 *   appCamIntensity      — 'subtle' | 'standard' | 'vivid'  (default 'standard')
 *       Saturation "slider". Same hue identity across all three stops so an
 *       operator's muscle memory for "which color is which camera" is preserved.
 *   appCamColorblindSafe — boolean stored as 'true' | 'false'  (default false)
 *       Swaps the cam palette to the Wong colorblind-safe hue family. Independent
 *       of intensity and WINS over it (see brand.css source-order note).
 *
 * Both are applied as data-cam-intensity / data-cam-cb-safe attributes on
 * <html> + <body> — exactly like theme.js applies data-theme — so the --cam-*
 * tokens in brand.css resolve before React mounts and survive logout. This module
 * keeps BOTH attributes set at all times so the CSS cascade can decide the winner.
 *
 * Stored in localStorage (not Redux) for the same reasons as theme.js: available
 * pre-mount, survives logout, no flash of wrong palette.
 */

const CAM_INTENSITY_KEY = 'appCamIntensity';
const CAM_CB_SAFE_KEY = 'appCamColorblindSafe';

export const CAM_INTENSITIES = ['subtle', 'standard', 'vivid'];
const DEFAULT_INTENSITY = 'standard';

/**
 * Current cam-color intensity. Falls back to the default for a missing or
 * unrecognized stored value.
 */
export const getCamIntensity = () => {
  const stored = localStorage.getItem(CAM_INTENSITY_KEY);
  return CAM_INTENSITIES.includes(stored) ? stored : DEFAULT_INTENSITY;
};

/**
 * Colorblind-safe preference as a boolean. Default false.
 */
export const getCamColorblindSafe = () => {
  return localStorage.getItem(CAM_CB_SAFE_KEY) === 'true';
};

/**
 * Persist the intensity (validated) and re-apply. Returns the value stored.
 */
export const setCamIntensity = (intensity) => {
  const next = CAM_INTENSITIES.includes(intensity) ? intensity : DEFAULT_INTENSITY;
  localStorage.setItem(CAM_INTENSITY_KEY, next);
  applyCamTheme();
  return next;
};

/**
 * Persist the colorblind-safe boolean and re-apply. Returns the boolean stored.
 */
export const setCamColorblindSafe = (enabled) => {
  const next = !!enabled;
  localStorage.setItem(CAM_CB_SAFE_KEY, next ? 'true' : 'false');
  applyCamTheme();
  return next;
};

/**
 * Apply both prefs to the DOM: set data-cam-intensity + data-cam-cb-safe on
 * <html> and <body>, then dispatch a 'camthemechange' event (mirrors theme.js's
 * 'themechange') so React components can react if needed. Returns the applied
 * { intensity, cbSafe }.
 */
export const applyCamTheme = () => {
  const intensity = getCamIntensity();
  const cbSafe = getCamColorblindSafe();
  // <html> (so :root tokens resolve) + <body> (belt-and-suspenders, mirrors theme.js).
  [document.documentElement, document.body].forEach((el) => {
    if (!el) return;
    el.setAttribute('data-cam-intensity', intensity);
    el.setAttribute('data-cam-cb-safe', cbSafe ? 'true' : 'false');
  });
  window.dispatchEvent(new CustomEvent('camthemechange', { detail: { intensity, cbSafe } }));
  return { intensity, cbSafe };
};

/**
 * Initialize cam-color prefs on app startup (call beside initTheme). Applies the
 * stored values so a non-default palette shows immediately on reload.
 */
export const initCamTheme = () => applyCamTheme();
